import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Users, Baby, Search, ChevronLeft, ChevronRight, Loader2, Edit, DollarSign, Mail, Phone, MapPin, School, ArrowUpDown } from 'lucide-react';

export default function AdminSawyerDataPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('customers');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Customers
  const [customers, setCustomers] = useState([]);
  const [custTotal, setCustTotal] = useState(0);
  const [custPage, setCustPage] = useState(1);
  const [custSearch, setCustSearch] = useState('');
  const [custSort, setCustSort] = useState('total_spend');
  const [custOrder, setCustOrder] = useState('desc');

  // Children
  const [children, setChildren] = useState([]);
  const [childTotal, setChildTotal] = useState(0);
  const [childPage, setChildPage] = useState(1);
  const [childSearch, setChildSearch] = useState('');

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editType, setEditType] = useState('');

  // Detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Inline edit
  const [editingCell, setEditingCell] = useState(null);

  // Migration
  const [mappings, setMappings] = useState([]);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchCustomers(1, '', 'total_spend', 'desc');
    fetchChildren(1, '');
    fetchMappings();
    fetchMigrationStatus();
  }, []);

  const fetchStats = async () => {
    try { const r = await api.get('/sawyer-data/customers/stats'); setStats(r.data); } catch {}
    finally { setLoading(false); }
  };

  const fetchCustomers = async (page = 1, search = '', sort = 'total_spend', order = 'desc') => {
    try {
      const r = await api.get('/sawyer-data/customers', { params: { page, page_size: 50, search, sort, order } });
      setCustomers(r.data.rows); setCustTotal(r.data.total);
    } catch { setCustomers([]); }
  };

  const fetchChildren = async (page = 1, search = '') => {
    try {
      const r = await api.get('/sawyer-data/children', { params: { page, page_size: 50, search } });
      setChildren(r.data.rows); setChildTotal(r.data.total);
    } catch { setChildren([]); }
  };

  const fetchMappings = async () => {
    try { const r = await api.get('/migration/course-mappings'); setMappings(r.data); } catch {}
  };

  const fetchMigrationStatus = async () => {
    try { const r = await api.get('/migration/status'); setMigrationStatus(r.data); } catch {}
  };

  const updateMapping = async (id, field, value) => {
    try {
      await api.patch(`/migration/course-mappings/${id}`, { [field]: value });
      toast.success('Mapping updated');
      await fetchMappings();
    } catch { toast.error('Failed'); }
  };

  const runMigration = async () => {
    if (!window.confirm('This will create RSC Users (Parents), Learners, Courses, Sections, and Enrollments from your Sawyer data. Proceed?')) return;
    setMigrating(true);
    try {
      const r = await api.post('/migration/migrate');
      toast.success(r.data.summary);
      await fetchMigrationStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Migration failed'); }
    finally { setMigrating(false); }
  };

  const openDetail = async (custId) => {
    try {
      const r = await api.get(`/sawyer-data/customers/${custId}`);
      setDetailData(r.data); setDetailOpen(true);
    } catch { toast.error('Failed to load details'); }
  };

  const openEdit = (row, type) => { setEditData({ ...row }); setEditType(type); setEditOpen(true); };

  const saveEdit = async () => {
    if (!editData) return;
    try {
      const { id, ...data } = editData;
      if (editType === 'customer') {
        await api.patch(`/sawyer-data/customers/${id}/edit`, data);
        await fetchCustomers(custPage, custSearch, custSort, custOrder);
      } else {
        await api.patch(`/sawyer-data/children/${id}/edit`, data);
        await fetchChildren(childPage, childSearch);
      }
      setEditOpen(false);
      toast.success('Changes saved successfully');
    } catch (err) { toast.error('Save failed: ' + (err.response?.data?.detail || '')); }
  };

  const saveInline = async (id, field, value, type) => {
    try {
      if (type === 'customer') {
        await api.patch(`/sawyer-data/customers/${id}/edit`, { [field]: value });
        // Force re-fetch to show updated data
        await fetchCustomers(custPage, custSearch, custSort, custOrder);
      } else {
        await api.patch(`/sawyer-data/children/${id}/edit`, { [field]: value });
        await fetchChildren(childPage, childSearch);
      }
      setEditingCell(null);
      toast.success('Saved successfully');
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || 'Unknown error'));
      setEditingCell(null);
    }
  };

  const InlineCell = ({ id, field, value, type }) => {
    const isEditing = editingCell?.id === id && editingCell?.field === field;
    if (isEditing) return (
      <Input autoFocus defaultValue={editingCell.value} className="h-6 text-xs p-1 min-w-[80px]"
        onBlur={e => saveInline(id, field, e.target.value, type)}
        onKeyDown={e => { if (e.key === 'Enter') saveInline(id, field, e.target.value, type); if (e.key === 'Escape') setEditingCell(null); }} />
    );
    return <span className="cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1 rounded" onDoubleClick={() => setEditingCell({ id, field, value: value ?? '' })} title="Double-click to edit">{value ?? '-'}</span>;
  };

  const toggleSort = (col) => {
    const newOrder = custSort === col && custOrder === 'desc' ? 'asc' : 'desc';
    setCustSort(col); setCustOrder(newOrder); setCustPage(1);
    fetchCustomers(1, custSearch, col, newOrder);
  };

  const SortHeader = ({ col, label }) => (
    <TableHead className="text-xs cursor-pointer hover:text-primary select-none" onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">{label} {custSort === col && <ArrowUpDown className="h-3 w-3" />}</span>
    </TableHead>
  );

  const fmt = n => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6" /> Sawyer Data</h1>
        <p className="text-muted-foreground">Customer and child profiles imported from Sawyer — linked to bookings and transactions</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600">{stats.total?.toLocaleString()}</p><p className="text-xs text-muted-foreground">Customers</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-purple-600">{stats.total_children?.toLocaleString()}</p><p className="text-xs text-muted-foreground">Children</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">{stats.with_spend}</p><p className="text-xs text-muted-foreground">Paying Customers</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600">{fmt(stats.total_revenue)}</p><p className="text-xs text-muted-foreground">Total Spend</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-indigo-600">{fmt(stats.avg_spend)}</p><p className="text-xs text-muted-foreground">Avg Spend</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-cyan-600">{stats.email_opted_in}</p><p className="text-xs text-muted-foreground">Email Opted-In</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-amber-600">{stats.referred}</p><p className="text-xs text-muted-foreground">Referred</p></CardContent></Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers"><Users className="h-4 w-4 mr-1" /> Customers ({custTotal})</TabsTrigger>
          <TabsTrigger value="children"><Baby className="h-4 w-4 mr-1" /> Children ({childTotal})</TabsTrigger>
          <TabsTrigger value="migration"><ArrowUpDown className="h-4 w-4 mr-1" /> Migrate to RSC</TabsTrigger>
        </TabsList>

        {/* CUSTOMERS TAB */}
        <TabsContent value="customers" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, email, phone..." value={custSearch}
                onChange={e => { setCustSearch(e.target.value); setCustPage(1); fetchCustomers(1, e.target.value, custSort, custOrder); }} className="pl-10" />
            </div>
          </div>
          <div className="overflow-auto border rounded-lg max-h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-8"></TableHead>
                  <SortHeader col="first_name" label="Name" />
                  <SortHeader col="customer_email" label="Email" />
                  <TableHead className="text-xs">Phone</TableHead>
                  <TableHead className="text-xs">City</TableHead>
                  <SortHeader col="total_orders" label="Orders" />
                  <SortHeader col="total_spend" label="Total Spend" />
                  <TableHead className="text-xs">Avg</TableHead>
                  <TableHead className="text-xs">Children</TableHead>
                  <TableHead className="text-xs">Bookings</TableHead>
                  <TableHead className="text-xs">School</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map(c => (
                  <TableRow key={c.id} className="hover:bg-gray-50">
                    <TableCell className="p-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(c, 'customer')}><Edit className="h-3 w-3" /></Button>
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      <button className="text-blue-600 hover:underline" onClick={() => openDetail(c.id)}>
                        {c.first_name} {c.last_name}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs">{c.email}</TableCell>
                    <TableCell className="text-xs"><InlineCell id={c.id} field="phone" value={c.phone} type="customer" /></TableCell>
                    <TableCell className="text-xs"><InlineCell id={c.id} field="city" value={c.city} type="customer" /></TableCell>
                    <TableCell className="text-xs text-center">{c.total_orders || '-'}</TableCell>
                    <TableCell className="text-xs font-semibold text-green-600">{c.total_spend > 0 ? fmt(c.total_spend) : '-'}</TableCell>
                    <TableCell className="text-xs">{c.avg_spend > 0 ? fmt(c.avg_spend) : '-'}</TableCell>
                    <TableCell className="text-xs text-center">{c.child_count || '-'}</TableCell>
                    <TableCell className="text-xs text-center">{c.booking_count || '-'}</TableCell>
                    <TableCell className="text-xs truncate max-w-[120px]"><InlineCell id={c.id} field="school_name" value={c.school} type="customer" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Page {custPage} of {Math.ceil(custTotal / 50)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={custPage <= 1} onClick={() => { setCustPage(custPage-1); fetchCustomers(custPage-1, custSearch, custSort, custOrder); }}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={custPage >= Math.ceil(custTotal/50)} onClick={() => { setCustPage(custPage+1); fetchCustomers(custPage+1, custSearch, custSort, custOrder); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </TabsContent>

        {/* CHILDREN TAB */}
        <TabsContent value="children" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, school, parent..." value={childSearch}
                onChange={e => { setChildSearch(e.target.value); setChildPage(1); fetchChildren(1, e.target.value); }} className="pl-10" />
            </div>
          </div>
          <div className="overflow-auto border rounded-lg max-h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-8"></TableHead>
                  <TableHead className="text-xs">Child Name</TableHead>
                  <TableHead className="text-xs">Parent</TableHead>
                  <TableHead className="text-xs">Age</TableHead>
                  <TableHead className="text-xs">School</TableHead>
                  <TableHead className="text-xs">Gender</TableHead>
                  <TableHead className="text-xs">Shirt</TableHead>
                  <TableHead className="text-xs">Allergies</TableHead>
                  <TableHead className="text-xs">Photo</TableHead>
                  <TableHead className="text-xs">Bookings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {children.map(ch => (
                  <TableRow key={ch.id}>
                    <TableCell className="p-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(ch, 'child')}><Edit className="h-3 w-3" /></Button>
                    </TableCell>
                    <TableCell className="text-xs font-medium"><InlineCell id={ch.id} field="child_name" value={ch.name} type="child" /></TableCell>
                    <TableCell className="text-xs">{ch.parent_first} {ch.parent_last}</TableCell>
                    <TableCell className="text-xs text-center">{ch.age || '-'}</TableCell>
                    <TableCell className="text-xs"><InlineCell id={ch.id} field="school" value={ch.school} type="child" /></TableCell>
                    <TableCell className="text-xs"><InlineCell id={ch.id} field="gender" value={ch.gender} type="child" /></TableCell>
                    <TableCell className="text-xs">{ch.shirt_size || '-'}</TableCell>
                    <TableCell className="text-xs truncate max-w-[100px]"><InlineCell id={ch.id} field="allergies" value={ch.allergies} type="child" /></TableCell>
                    <TableCell className="text-xs">{ch.photo_consent ? <Badge className="bg-green-100 text-green-700 text-[10px]">Yes</Badge> : <Badge variant="outline" className="text-[10px]">No</Badge>}</TableCell>
                    <TableCell className="text-xs text-center">{ch.bookings || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Page {childPage} of {Math.ceil(childTotal / 50)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={childPage <= 1} onClick={() => { setChildPage(childPage-1); fetchChildren(childPage-1, childSearch); }}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={childPage >= Math.ceil(childTotal/50)} onClick={() => { setChildPage(childPage+1); fetchChildren(childPage+1, childSearch); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* MIGRATION TAB - rendered outside Tabs for space */}
      {activeTab === 'migration' && (
        <div className="space-y-6 mt-4">
          {/* Migration Status */}
          {migrationStatus && (
            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Source (Sawyer Data)</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="p-2 bg-blue-50 rounded text-center"><p className="text-lg font-bold text-blue-600">{migrationStatus.source.sawyer_customers}</p><p className="text-xs">Customers</p></div>
                  <div className="p-2 bg-purple-50 rounded text-center"><p className="text-lg font-bold text-purple-600">{migrationStatus.source.sawyer_children}</p><p className="text-xs">Children</p></div>
                  <div className="p-2 bg-indigo-50 rounded text-center"><p className="text-lg font-bold text-indigo-600">{migrationStatus.source.booking_enrollments}</p><p className="text-xs">Bookings</p></div>
                  <div className="p-2 bg-green-50 rounded text-center"><p className="text-lg font-bold text-green-600">{migrationStatus.source.valid_course_mappings}</p><p className="text-xs">Course Mappings</p></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Target (RSC Core Tables)</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="p-2 bg-green-50 rounded text-center"><p className="text-lg font-bold text-green-600">{migrationStatus.target.rsc_parents}</p><p className="text-xs">Parents (Users)</p></div>
                  <div className="p-2 bg-purple-50 rounded text-center"><p className="text-lg font-bold text-purple-600">{migrationStatus.target.rsc_learners}</p><p className="text-xs">Learners</p></div>
                  <div className="p-2 bg-blue-50 rounded text-center"><p className="text-lg font-bold text-blue-600">{migrationStatus.target.rsc_courses}</p><p className="text-xs">Courses</p></div>
                  <div className="p-2 bg-amber-50 rounded text-center"><p className="text-lg font-bold text-amber-600">{migrationStatus.target.rsc_enrollments}</p><p className="text-xs">Enrollments</p></div>
                </CardContent>
              </Card>
            </div>
          )}

          <Button onClick={runMigration} disabled={migrating} size="lg" className="w-full">
            {migrating ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <ArrowUpDown className="h-5 w-5 mr-2" />}
            {migrating ? 'Migrating...' : 'Run Migration: Sawyer → RSC'}
          </Button>

          {/* Course Name Mappings */}
          <Card>
            <CardHeader>
              <CardTitle>Course Name Mappings ({mappings.length})</CardTitle>
              <p className="text-sm text-muted-foreground">Review and fix how Sawyer activity names map to RSC courses. Edit clean names, mark locations/invalid entries as "Ignore".</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[500px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Sawyer Activity Name</TableHead>
                      <TableHead className="text-xs">→ RSC Course Name</TableHead>
                      <TableHead className="text-xs">Category</TableHead>
                      <TableHead className="text-xs">Bookings</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappings.map(m => (
                      <TableRow key={m.id} className={m.is_location || m.ignore ? 'bg-red-50/50 opacity-60' : ''}>
                        <TableCell className="text-xs">{m.raw_name}</TableCell>
                        <TableCell className="text-xs">
                          <Input
                            defaultValue={m.clean_name}
                            className="h-6 text-xs p-1"
                            onBlur={e => { if (e.target.value !== m.clean_name) updateMapping(m.id, 'clean_name', e.target.value); }}
                          />
                        </TableCell>
                        <TableCell className="text-xs">
                          <select className="h-6 text-xs border rounded px-1" defaultValue={m.category}
                            onChange={e => updateMapping(m.id, 'category', e.target.value)}>
                            <option value="">—</option>
                            <option value="coding">Coding</option>
                            <option value="robotics">Robotics</option>
                            <option value="engineering">Engineering</option>
                            <option value="math">Math</option>
                            <option value="science">Science</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-xs text-center">{m.bookings}</TableCell>
                        <TableCell className="text-xs">
                          {m.is_location || m.ignore ? (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600" onClick={() => updateMapping(m.id, 'ignore_row', false)}>Ignored — Click to restore</Button>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => updateMapping(m.id, 'ignore_row', true)}>Ignore</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit {editType === 'customer' ? 'Customer' : 'Child'}</DialogTitle></DialogHeader>
          {editData && (
            <div className="grid grid-cols-2 gap-3 py-2">
              {Object.entries(editData).filter(([k]) => !['id','total_orders','total_spend','avg_spend','total_transactions','child_count','booking_count','bookings','profile_created','email_consent','email','parent_first','parent_last','parent_phone','activities','photo_consent','birthday','age'].includes(k)).map(([k, v]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs capitalize">{k.replace(/_/g, ' ')}</Label>
                  <Input value={v ?? ''} onChange={e => setEditData(p => ({...p, [k]: e.target.value}))} className="h-8 text-sm" />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CUSTOMER DETAIL DIALOG */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle>{detailData.customer.first_name} {detailData.customer.last_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Contact Info */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" />{detailData.customer.customer_email}</div>
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" />{detailData.customer.phone || '-'}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" />{detailData.customer.city} {detailData.customer.state}</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-green-600">${Number(detailData.customer.total_spend || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Spend</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-600">{detailData.customer.total_orders || 0}</p><p className="text-xs text-muted-foreground">Orders</p></CardContent></Card>
                  <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-purple-600">{detailData.children?.length || 0}</p><p className="text-xs text-muted-foreground">Children</p></CardContent></Card>
                </div>
                {/* Children */}
                {detailData.children?.length > 0 && (
                  <div><h4 className="font-semibold mb-2">Children</h4>
                    <div className="space-y-2">{detailData.children.map((ch, i) => (
                      <div key={i} className="p-2 bg-gray-50 rounded flex items-center gap-4 text-sm">
                        <span className="font-medium">{ch.child_name}</span>
                        <span className="text-muted-foreground">Age {ch.age || '?'}</span>
                        <span className="text-muted-foreground">{ch.school || ''}</span>
                        {ch.allergies && <Badge variant="destructive" className="text-[10px]">{ch.allergies}</Badge>}
                      </div>
                    ))}</div>
                  </div>
                )}
                {/* Recent Bookings */}
                {detailData.bookings?.length > 0 && (
                  <div><h4 className="font-semibold mb-2">Recent Bookings ({detailData.bookings.length})</h4>
                    <div className="max-h-[200px] overflow-auto border rounded">
                      <Table><TableBody>{detailData.bookings.map((b, i) => (
                        <TableRow key={i}><TableCell className="text-xs">{b.child}</TableCell><TableCell className="text-xs font-medium">{b.course}</TableCell><TableCell className="text-xs">{b.location}</TableCell><TableCell className="text-xs">{b.date}</TableCell></TableRow>
                      ))}</TableBody></Table>
                    </div>
                  </div>
                )}
                {/* Recent Transactions */}
                {detailData.transactions?.length > 0 && (
                  <div><h4 className="font-semibold mb-2">Recent Transactions ({detailData.transactions.length})</h4>
                    <div className="max-h-[150px] overflow-auto border rounded">
                      <Table><TableBody>{detailData.transactions.map((t, i) => (
                        <TableRow key={i}><TableCell className="text-xs">{t.activity?.substring(0, 40)}</TableCell><TableCell className="text-xs font-semibold text-green-600">${t.checkout?.toFixed(2)}</TableCell><TableCell className="text-xs">{t.date}</TableCell><TableCell className="text-xs">{t.type}</TableCell></TableRow>
                      ))}</TableBody></Table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
